import { useMemo, useState } from "react";
import { categoryList, newsItems, type NewsItem } from "@/mocks/news";

export default function Feed() {
  const [cat, setCat] = useState("全部");
  const [kw, setKw] = useState("");
  const [liked, setLiked] = useState<Record<number, boolean>>({});

  const list = useMemo<NewsItem[]>(() => {
    let arr = cat === "全部" ? newsItems : newsItems.filter((n) => n.category === cat);
    const q = kw.trim();
    if (q) arr = arr.filter((n) => n.title.includes(q) || n.tag.includes(q) || n.excerpt.includes(q));
    return arr;
  }, [cat, kw]);

  const toggleLike = (id: number) => {
    setLiked((p) => ({ ...p, [id]: !p[id] }));
  };

  return (
    <section id="today" className="scroll-mt-20 bg-background-100/60">
      <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        {/* section header */}
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-fire-fill text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              持续更新中
            </span>
            <h2 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">今日瓜 · 大家都在聊什么</h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-700">
              娱乐现场、城市新鲜事、消费观察……分类浏览，或用关键词快速定位你关心的那一条。
            </p>
          </div>

          <label className="relative block w-full md:w-72">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-600">
              <i className="ri-search-line text-base w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
            </span>
            <input
              type="search"
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              placeholder="搜索资讯关键词"
              className="w-full rounded-full border border-background-300/80 bg-background-50 py-2.5 pl-11 pr-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
            />
          </label>
        </div>

        {/* category tabs */}
        <div className="mt-7 flex flex-wrap items-center gap-2">
          {categoryList.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`cursor-pointer rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                cat === c
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-50 text-foreground-800 hover:bg-background-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* news grid */}
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((n) => {
            const isLiked = !!liked[n.id];
            return (
              <article
                key={n.id}
                className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-transform duration-200 hover:-translate-y-1"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={n.image}
                    alt={n.title}
                    className="aspect-[3/2] w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-950/75 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                    {n.category}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-bold text-foreground-950">
                    {n.tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <h3 className="text-base font-semibold leading-snug text-foreground-950 md:text-[17px]">
                    <a href="#today" className="hover:text-primary-600">{n.title}</a>
                  </h3>
                  <p className="mt-2.5 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{n.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-3">
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true"></i>
                        {n.time}
                      </span>
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true"></i>
                        {n.views}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleLike(n.id)}
                      aria-label={isLiked ? "取消收藏" : "收藏本条资讯"}
                      className={`flex cursor-pointer items-center gap-1 whitespace-nowrap transition-colors ${
                        isLiked ? "text-primary-500" : "text-foreground-500 hover:text-primary-500"
                      }`}
                    >
                      <i className={`${isLiked ? "ri-heart-fill" : "ri-heart-line"} w-3.5 h-3.5 flex items-center justify-center`}></i>
                      {(n.likes + (isLiked ? 1 : 0)).toLocaleString()}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {list.length === 0 && (
          <div className="mt-8 rounded-xl border border-dashed border-background-300 bg-background-50 py-14 text-center">
            <i className="ri-inbox-2-line text-3xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-600">没有匹配“{kw}”的资讯，换个关键词试试</p>
          </div>
        )}
      </div>
    </section>
  );
}